// Cash settlements in the Activity ledger (Kareem, 2026-10-23: "the 777
// payment to Beko does not show up on his activity page").
//
// The ledger lists money movements (value events, silent movements) and,
// since 2026-10-21, pool entries. A cash settlement — the investor's payment
// to Kareem, Kareem's cash-out payout to the investor, a waiver — is an
// investment_settlements record with no movement behind it, so it only ever
// showed on the Settlements page and the Home settlements strip. Activity is
// meant to be the one complete history, so settlements render there too, as
// value-less rows in the activityRows.js shape, with no custody split.
//
// Rejected and removed rows stay out (the Settlements page keeps them as
// records with their note). Pending ones show as "Awaiting Kareem".

const TYPES = {
  payout:  { flow: 'Payout from Kareem', src: 'kareem', srcLabel: 'Kareem', dst: 'cash_payout', dstLabel: 'Payout to you', sign: 1 },
  payment: { flow: 'Payment to Kareem', src: 'cash_payment', srcLabel: 'Your payment', dst: 'kareem', dstLabel: 'Kareem', sign: -1 },
  waiver:  { flow: 'Waived by Kareem', src: 'kareem', srcLabel: 'Kareem', dst: 'waiver', dstLabel: 'Waiver', sign: 0 },
};

export function settlementType(s) {
  if ((s.kind || 'cash') === 'waiver') return 'waiver';
  return (s.direction || 'to_kareem') === 'to_investor' ? 'payout' : 'payment';
}

export const SETTLEMENT_HIDDEN = ['rejected', 'removed', 'voided'];

// ctx: { dateLabel, holderId? } — holderId pins the row to the household
// head in the household view; the admin view uses the row's own household.
export function shapeSettlementRow(s, ctx) {
  const type = settlementType(s);
  const meta = TYPES[type];
  const member = ctx.holderId || s.enrolled_member_id;
  return {
    id: 'settle-' + s.id,
    settlementId: s.id,
    settleType: type,
    settleSign: meta.sign,
    method: s.payment_method || 'Other',
    memo: s.memo || '',
    eventDate: s.settle_date,
    date: ctx.dateLabel(s.settle_date),
    member,
    household: s.enrolled_member_id || member,
    flow: meta.flow,
    srcKind: meta.src, srcLabel: meta.srcLabel,
    dstKind: meta.dst, dstLabel: meta.dstLabel,
    custody: null,
    amount: Math.abs(Number(s.amount || 0)),
    rate: 0, value: 0, kareem: 0, investor: 0, kareemShare: 0,
    stepId: null, movementId: null,
    // A payment is the investor's own record unless an admin stamped it;
    // payouts and waivers are always Kareem's act.
    enteredByRole: type === 'payment' ? (s.created_by_role || 'executor') : 'admin',
    enteredByMemberId: type === 'payment' ? (s.created_by_member_id || null) : null,
    movementStatus: s.status || 'pending',
    noValue: true,
  };
}

export function settlementRows(list, ctx) {
  return (list || [])
    .filter((s) => s && s.id && !SETTLEMENT_HIDDEN.includes(s.status || 'pending'))
    .map((s) => shapeSettlementRow(s, ctx));
}

// Row copy shared by the three surfaces.
export function settlementSub(e, viewerIsInvestor, name) {
  const who = viewerIsInvestor ? 'you' : name;
  if (e.settleType === 'payout') return `Kareem paid ${who} · ${e.method}`;
  if (e.settleType === 'payment') return `${viewerIsInvestor ? 'You' : name} paid Kareem · ${e.method}`;
  return `Kareem waived this for ${who}`;
}
export function settlementAmount(e) {
  const n = '$' + Number(e.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return e.settleSign > 0 ? '+' + n : e.settleSign < 0 ? '−' + n : n;
}
